<template>
  <!-- La raíz queda en su lugar; el popup se muda a <body> (la transición de rutas deja
       `transform` en la página y un fixed adentro no cubriría la pantalla). -->
  <span class="dwc-anchor">
    <div v-show="value" ref="overlay" class="dwc" @keydown.esc="close">
      <div class="dwc__backdrop" @click="close"></div>
      <div class="dwc__panel" role="dialog" aria-modal="true" :aria-label="title || $tn('dashboardWidgets.catalog.title')">
        <header class="dwc__head">
          <div class="dwc__titles">
            <h2 class="dwc__title">{{ title || $tn("dashboardWidgets.catalog.title") }}</h2>
            <p class="dwc__subtitle">{{ subtitle || $tn("dashboardWidgets.catalog.subtitle") }}</p>
          </div>
          <button type="button" class="dwc__close" :aria-label="$tn('dashboardWidgets.catalog.close')" @click="close">
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </header>

        <div class="dwc__tools">
          <div class="dwc__search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              ref="search"
              v-model="query"
              type="search"
              :placeholder="$tn('dashboardWidgets.catalog.search')"
              :aria-label="$tn('dashboardWidgets.catalog.search')"
            />
          </div>
          <div class="dwc__cats" role="tablist">
            <button
              v-for="c in categories"
              :key="c.key"
              type="button"
              role="tab"
              class="dwc__cat"
              :class="{ 'is-active': category === c.key }"
              :aria-selected="category === c.key ? 'true' : 'false'"
              @click="category = c.key"
            >
              {{ c.label }} <span class="dwc__cat-count">{{ c.count }}</span>
            </button>
          </div>
        </div>

        <div class="dwc__body">
          <p v-if="loading" class="dwc__empty">{{ $tn("dashboardWidgets.catalog.loading") }}</p>
          <p v-else-if="!filtered.length" class="dwc__empty">{{ $tn("dashboardWidgets.catalog.noResults") }}</p>
          <div v-else class="dwc__grid">
            <article
              v-for="e in filtered"
              :key="e.type"
              class="dwc-card"
              :class="{ 'is-added': isAdded(e) && !e.multiple }"
            >
              <div class="dwc-card__preview">
                <WidgetPreview :kind="e.preview" />
                <span v-if="isAdded(e)" class="dwc-card__badge">
                  <i class="fas fa-check" aria-hidden="true"></i> {{ $tn("dashboardWidgets.catalog.added") }}
                </span>
              </div>
              <div class="dwc-card__info">
                <div class="dwc-card__name">
                  <i class="fas" :class="`fa-${e.icon}`" aria-hidden="true"></i>
                  <span :title="titleOf(e)">{{ titleOf(e) }}</span>
                </div>
                <p class="dwc-card__desc">{{ descriptionOf(e) }}</p>
              </div>
              <div class="dwc-card__actions">
                <button
                  v-if="isAdded(e) && !e.multiple"
                  type="button"
                  class="btn btn-sm btn-outline-danger w-100"
                  @click="$emit('remove', e)"
                >
                  {{ $tn("dashboardWidgets.catalog.remove") }}
                </button>
                <button v-else type="button" class="btn btn-sm btn-primary w-100" @click="$emit('add', e)">
                  <i class="fas fa-plus" aria-hidden="true"></i>
                  {{ isAdded(e) ? $tn("dashboardWidgets.catalog.addAnother") : $tn("dashboardWidgets.catalog.add") }}
                </button>
              </div>
            </article>
          </div>
        </div>

        <footer class="dwc__foot">
          <button type="button" class="btn btn-sm btn-link text-secondary" @click="$emit('reset')">
            <i class="fas fa-undo" aria-hidden="true"></i> {{ $tn("dashboardWidgets.catalog.reset") }}
          </button>
          <button type="button" class="btn btn-sm btn-primary" @click="close">{{ $tn("dashboardWidgets.catalog.done") }}</button>
        </footer>
      </div>
    </div>
  </span>
</template>

<script>
/**
 * Popup para agregar o quitar widgets: cada widget disponible con su miniatura.
 * En el teléfono ocupa toda la pantalla.
 */
import WidgetPreview from "./WidgetPreview.vue";
import { WIDGET_CATEGORIES } from "./widgetCatalog";

export default {
  name: "DashboardWidgetCatalog",
  components: { WidgetPreview },
  props: {
    value: { type: Boolean, default: false },
    entries: { type: Array, default: () => [] },
    addedTypes: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    /** Para usarlo fuera del dashboard (p. ej. la ficha de un contacto). */
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
  },
  data() {
    return { query: "", category: "all" };
  },
  computed: {
    categories() {
      const count = (key) => this.entries.filter((e) => key === "all" || e.category === key).length;
      return ["all", ...WIDGET_CATEGORIES]
        .map((key) => ({ key, label: this.$tn(`dashboardWidgets.categories.${key}`), count: count(key) }))
        .filter((c) => c.count > 0);
    },
    filtered() {
      const q = this.query.trim().toLowerCase();
      return this.entries.filter((e) => {
        if (this.category !== "all" && e.category !== this.category) return false;
        if (!q) return true;
        return `${this.titleOf(e)} ${this.descriptionOf(e)}`.toLowerCase().includes(q);
      });
    },
  },
  watch: {
    value(open) {
      document.body.classList.toggle("dwc-open", !!open);
      if (open) this.$nextTick(() => this.$refs.search && !this.isTouch() && this.$refs.search.focus());
    },
  },
  mounted() {
    document.body.appendChild(this.$refs.overlay);
  },
  beforeDestroy() {
    document.body.classList.remove("dwc-open");
    const el = this.$refs.overlay;
    if (el && el.parentNode) el.parentNode.removeChild(el);
  },
  methods: {
    isTouch() {
      return typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
    },
    i18nKey(e) {
      return `dashboardWidgets.items.${e.type.replace(/[:-]/g, "_")}`;
    },
    titleOf(e) {
      return e.title || this.$tn(`${this.i18nKey(e)}.title`);
    },
    descriptionOf(e) {
      if (e.description && e.kind !== "report") return e.description;
      if (e.kind === "report") {
        return e.description || this.$tn("dashboardWidgets.items.report.description", { model: e.modelName || "" });
      }
      return this.$tn(`${this.i18nKey(e)}.description`);
    },
    isAdded(e) {
      return this.addedTypes.includes(e.type);
    },
    close() {
      this.$emit("input", false);
    },
  },
};
</script>

<style>
body.dwc-open { overflow: hidden !important; }
</style>

<style scoped>
.dwc-anchor { display: contents; }
.dwc { position: fixed; inset: 0; z-index: 1060; display: flex; align-items: center; justify-content: center; padding: 24px; }
.dwc__backdrop { position: absolute; inset: 0; background: rgba(var(--bs-black-rgb), 0.45); }
.dwc__panel {
  position: relative;
  width: min(1040px, 100%);
  max-height: min(860px, 100%);
  display: flex;
  flex-direction: column;
  background: var(--dw-surface);
  color: var(--dw-text);
  border-radius: 16px;
  box-shadow: var(--dw-shadow-lg);
  overflow: hidden;
}
.dwc__head { display: flex; align-items: flex-start; gap: 12px; padding: 18px 20px 8px; }
.dwc__titles { flex: 1; min-width: 0; }
.dwc__title { margin: 0; font-size: 1.15rem; font-weight: 700; }
.dwc__subtitle { margin: 2px 0 0; font-size: 0.85rem; color: var(--dw-muted); }
.dwc__close {
  border: 0;
  background: transparent;
  color: var(--dw-muted);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
}
.dwc__close:hover { background: var(--dw-surface-2); color: var(--dw-text); }
.dwc__tools { padding: 8px 20px 12px; display: flex; flex-direction: column; gap: 10px; border-bottom: 1px solid var(--dw-border); }
.dwc__search {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--dw-border);
  border-radius: 10px;
  padding: 0 12px;
  background: var(--dw-surface-2);
}
.dwc__search i { color: var(--dw-muted); }
.dwc__search input { flex: 1; border: 0; background: transparent; padding: 9px 0; outline: none; color: var(--dw-text); min-width: 0; }
.dwc__cats { display: flex; gap: 6px; overflow-x: auto; scrollbar-width: none; }
.dwc__cats::-webkit-scrollbar { display: none; }
.dwc__cat {
  flex-shrink: 0;
  border: 1px solid var(--dw-border);
  background: var(--dw-surface);
  color: var(--dw-text);
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 0.82rem;
  cursor: pointer;
}
.dwc__cat.is-active { background: var(--bs-primary); border-color: var(--bs-primary); color: var(--bs-white); }
.dwc__cat-count { opacity: 0.7; margin-left: 2px; font-size: 0.75rem; }
.dwc__body { flex: 1; min-height: 0; overflow-y: auto; padding: 16px 20px; background: var(--dw-surface-2); }
.dwc__empty { text-align: center; color: var(--dw-muted); padding: 32px 0; margin: 0; }
.dwc__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 14px; }
.dwc-card {
  display: flex;
  flex-direction: column;
  background: var(--dw-surface);
  border: 1px solid var(--dw-border);
  border-radius: 12px;
  overflow: hidden;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}
.dwc-card:hover { box-shadow: var(--bs-box-shadow); transform: translateY(-1px); }
.dwc-card.is-added { border-color: rgba(var(--bs-primary-rgb), 0.55); }
.dwc-card__preview {
  position: relative;
  height: 120px;
  border-bottom: 1px solid var(--dw-border);
  background: var(--dw-surface);
}
.dwc-card__badge {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--bs-primary);
  color: var(--bs-white);
}
.dwc-card__info { padding: 10px 12px 4px; flex: 1; min-width: 0; }
.dwc-card__name { display: flex; align-items: center; gap: 8px; font-weight: 600; font-size: 0.9rem; min-width: 0; }
.dwc-card__name i { color: var(--bs-primary); }
.dwc-card__name span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dwc-card__desc {
  margin: 4px 0 0;
  font-size: 0.78rem;
  color: var(--dw-muted);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.dwc-card__actions { padding: 8px 12px 12px; }
.dwc__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 20px;
  border-top: 1px solid var(--dw-border);
}

@media (max-width: 767.98px) {
  .dwc { padding: 0; align-items: stretch; }
  .dwc__panel { width: 100%; max-height: none; height: 100%; border-radius: 0; }
  .dwc__head { padding: 14px 16px 6px; }
  .dwc__tools { padding: 6px 16px 10px; }
  .dwc__body { padding: 12px 16px; }
  .dwc__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .dwc-card__preview { height: 86px; }
  .dwc-card__desc { display: none; }
  .dwc-card__info { padding: 8px 10px 2px; }
  .dwc-card__name { font-size: 0.82rem; }
  .dwc-card__actions { padding: 6px 10px 10px; }
  .dwc__foot { padding: 10px 16px calc(10px + env(safe-area-inset-bottom)); }
}
</style>
